import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { fetchPlayerProfileLink } from './utils/fetch-player-profile-link';

/**
 * Renders a player profile link with automatic external link detection and tracking.
 *
 * When a player is from a different club than the host site, the link redirects to
 * afl.com.au and opens in a new tab. For AFLW external players, URLs use the
 * /aflw/players/ format instead of /players/aflw/.
 *
 * @param {object} props - The component props
 * @param {object} props.firstPlayer - Alternative player object (used in some tracking contexts)
 * @param {object} props.player - The player object containing playerId, firstName, and surname
 * @param {string} props.player.playerId - The player's unique identifier
 * @param {string} props.player.firstName - The player's first name
 * @param {string} props.player.surname - The player's surname
 * @param {boolean} props.isAFLW - Whether this is an AFLW match (determines URL format for external links)
 * @param {string} props.classes - CSS classes to apply to the link element
 * @param {object} props.trackingInfo - Tracking configuration object
 * @param {string} props.trackingInfo.event - Tracking event type (SELECT.LINK, SELECT.CTA, etc.)
 * @param {string} props.trackingInfo.component_name - Name of the component for tracking
 * @param {string} props.trackingInfo.component_content_type - Content type for tracking
 * @param {number} props.trackingInfo.index - Position index for tracking
 * @param {number} props.trackingInfo.count - Total count for tracking
 * @param {string} props.trackingInfo.searchTerm - Search term for player search tracking
 * @param {string} props.trackingInfo.linkText - Link text for CTA tracking
 * @param {boolean} props.isExternal - Force external link behavior (opens in new tab)
 * @param {React.ReactNode} props.children - Child elements to render inside the link
 * @param {string} props.clubId - The player's club/team ID (used to determine if external)
 * @returns {React.JSX.Element} - The rendered link element with tracking
 */
export const PlayerProfileLink = ({
    firstPlayer = {},
    player = {},
    isAFLW,
    classes,
    trackingInfo,
    isExternal = false,
    children,
    clubId
}) => {
    const [playerURL, setPlayerURL] = useState('');

    useEffect(() => {
        if (player) {
            fetchPlayerProfileLink(player, clubId, isAFLW)
                .then(setPlayerURL) // Or setPlayerURL in PlayerProfileLink.jsx
                .catch((err) =>
                    console.error('Error setting player URL:', err)
                );
        }
    }, [player, isAFLW, clubId]);

    /**
     * Link click handler
     * triggers various tracking functions depending on the `trackingInfo` values
     */
    const onClick = () => {
        /* eslint-disable camelcase */
        if (trackingInfo?.searchTerm) {
            // tracking in context of players search
            PULSE.app.analytics.playerSelectionTracking(
                trackingInfo.searchTerm,
                playerURL,
                trackingInfo.component_name,
                `${player?.firstName} ${player?.surname}`,
                trackingInfo.count,
                ''
            );
        } else if (trackingInfo) {
            if (
                trackingInfo.event ===
                PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.LINK
            ) {
                // tracking as select_link
                const url = new URL(playerURL, location.origin);
                PULSE.app.tracking.recordEvent(
                    PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.LINK,
                    {
                        eventData: {
                            link_text: `${player?.firstName} ${player?.surname}`,
                            link_url: playerURL,
                            outbound: url.origin !== location.origin,
                            type:
                                url.origin !== location.origin
                                    ? 'external'
                                    : 'internal',
                            component_name:
                                trackingInfo.component_name || 'team lineups',
                            component_content_type:
                                trackingInfo.component_content_type
                        }
                    }
                );
            } else if (
                trackingInfo.event ===
                PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.CTA
            ) {
                // tracking as select_cta
                PULSE.app.tracking.recordEvent(
                    PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.CTA,
                    {
                        eventData: {
                            link_text: trackingInfo.linkText,
                            link_url: playerURL,
                            outbound: false,
                            component_name: trackingInfo.component_name,
                            component_type: 'player profile button',
                            component_content_type: 'player profile'
                        }
                    }
                );
            } else {
                // tracking as select_card
                PULSE.app.tracking.selectPlayerCardHandler(
                    firstPlayer?.player?.givenName
                        ? firstPlayer?.player?.givenName +
                              ' ' +
                              firstPlayer?.player?.surname
                        : player?.firstName + ' ' + player?.surname,
                    trackingInfo?.component_name,
                    PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES.PLAYER,
                    `${player?.firstName} ${player?.surname}`,
                    playerURL,
                    trackingInfo?.index,
                    trackingInfo?.count
                );
            }
        }
        /* eslint-enable camelcase */
    };

    // Check if this should be an external link (player from different club)
    const isPlayerFromDifferentClub =
        PULSE.app.common.url.isPlayerFromDifferentClub(clubId);

    const shouldOpenInNewTab = isExternal || isPlayerFromDifferentClub;

    return playerURL ? (
        <a
            className={classes}
            href={playerURL}
            onClick={onClick}
            target={shouldOpenInNewTab ? '_blank' : undefined}
            rel={shouldOpenInNewTab ? 'noreferrer' : undefined}
        >
            {children}
        </a>
    ) : (
        children
    );
};

/* eslint-disable camelcase */
PlayerProfileLink.propTypes = {
    firstPlayer: PropTypes.object,
    player: PropTypes.shape({
        playerId: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        surname: PropTypes.string.isRequired
    }).isRequired,
    isAFLW: PropTypes.bool,
    classes: PropTypes.string,
    trackingInfo: PropTypes.shape({
        event: PropTypes.string,
        component_name: PropTypes.string,
        component_content_type: PropTypes.string,
        index: PropTypes.number,
        count: PropTypes.number,
        searchTerm: PropTypes.string,
        linkText: PropTypes.string
    }),
    children: PropTypes.any,
    isExternal: PropTypes.bool,
    clubId: PropTypes.string
};
